import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { consumeGoogleNext } from "@/lib/googleAuth";

/**
 * Handles the return from Emergent Google OAuth.
 * The OAuth flow appends `#session_id=…` to the URL fragment; we strip it,
 * exchange it for a server session, then redirect to wherever the user was
 * originally headed (stashed in sessionStorage before the round trip).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { exchangeGoogleSession } = useAuth();
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setError("Missing session_id");
      return;
    }

    (async () => {
      try {
        const user = await exchangeGoogleSession(sessionId);
        toast.success("Welcome.");
        const dest = consumeGoogleNext();
        // wipe the fragment from the URL before we hand off
        window.history.replaceState(null, "", dest);
        navigate(dest, { replace: true, state: { user } });
      } catch (err) {
        setError(formatApiErrorDetail(err));
      }
    })();
  }, [exchangeGoogleSession, navigate, location.hash]);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6"
      style={{ backgroundColor: "#080419", color: "#F5EFE3", fontFamily: "'Courier Prime', monospace" }}
      data-testid="auth-callback"
    >
      <Toaster position="bottom-right" richColors />
      {error ? (
        <div className="max-w-md text-center">
          <p className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Sign-in didn&apos;t go through.
          </p>
          <p className="mt-2 text-sm" style={{ color: "rgba(245,239,227,0.7)" }}>
            {error}
          </p>
          <button
            type="button"
            onClick={() => navigate("/app/login", { replace: true })}
            className="mt-5 rounded-full px-5 h-11 text-xs uppercase tracking-[0.22em] border border-white/30"
            data-testid="auth-callback-retry"
          >
            Try again
          </button>
        </div>
      ) : (
        <p className="text-sm" style={{ color: "rgba(245,239,227,0.6)" }} data-testid="auth-callback-loading">
          Signing you in…
        </p>
      )}
    </div>
  );
}
